import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, Chip, IconButton, Snackbar, Tab, Tabs } from '@mui/material';
import { ChatBubbleOutlined, Close, Folder, History, Mail } from '@mui/icons-material';
import { useQuery } from '@apollo/client/react';
import { GET_ACCESSES, GET_LICENSES_WHERE_IS_ADMIN, GET_USER } from '../user.graphql';
import { GET_ACCESSES_FOR_USER } from '../../pdf/pdf.graphql';
import { User } from '../../interfaces';
import { UserAccess } from '../interfaces';
import { Hero } from './Hero';
import { ProfilePanel } from './ProfilePanel';
import { AdminLicensesPanel } from './AdminLicensesPanel';
import { AccessesPanel } from './AccessesPanel';
import { ChangesPanel } from './ChangesPanel';
import { CommentsPanel } from './CommentsPanel';
import { NotificationsPanel } from './NotificationsPanel';
import { tokens } from './tokens';

type TabId = 'access' | 'changes' | 'mail' | 'comments';

export default function Details() {
  const params = useParams<{ id: string }>();
  const userId = Number(params.id);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: TabId =
    tabParam === 'changes' || tabParam === 'mail' || tabParam === 'comments' ? tabParam : 'access';
  const [emailErrorOpen, setEmailErrorOpen] = useState(false);

  const userQuery = useQuery<{ user: User }>(GET_USER, { variables: { id: userId } });
  const accessesQuery = useQuery<{ userAccess: UserAccess[] }>(GET_ACCESSES, { variables: { id: userId } });
  const singleDocAccessesQuery = useQuery<{ accesses: { id: number }[] }>(GET_ACCESSES_FOR_USER, { variables: { userId } });
  const adminQuery = useQuery<{ licenses: { id: number; companyName: string }[] }>(GET_LICENSES_WHERE_IS_ADMIN, {
    variables: { id: userId },
  });

  const activeAccesses = (accessesQuery.data?.userAccess ?? []).filter((x) => !x.isDeleted);
  const licenseGroupCount = new Set(activeAccesses.map((x) => x.license.id)).size;
  const totalAccessCount = activeAccesses.length;
  const singleDocAccessCount = singleDocAccessesQuery.data?.accesses.length ?? 0;
  const isActive = totalAccessCount + singleDocAccessCount > 0;
  const adminLicenseCount = adminQuery.data?.licenses.length ?? 0;

  const handleTabChange = (_: unknown, value: TabId) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'access') next.delete('tab');
    else next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  return (
    <Box
      sx={{
        padding: { xs: '12px 12px 48px', sm: '20px 20px 64px', md: '24px 32px 80px' },
        maxWidth: 1280,
        mx: 'auto',
        boxSizing: 'border-box',
      }}>
      {userQuery.error && <span>Feil! {userQuery.error.message}</span>}
      <Hero
        user={userQuery.data?.user}
        isActive={isActive}
        totalAccesses={totalAccessCount + singleDocAccessCount}
        licenseGroupCount={licenseGroupCount}
        adminLicenseCount={adminLicenseCount}
      />

      <Box
        sx={{
          mt: { xs: '12px', sm: '20px' },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(320px, 380px) 1fr' },
          gap: { xs: '12px', sm: '20px' },
          alignItems: 'start',
        }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '12px', sm: '20px' } }}>
          <ProfilePanel userId={userId} onLoginAlreadyUsed={() => setEmailErrorOpen(true)} />
          <AdminLicensesPanel userId={userId} />
        </Box>

        <Box
          sx={{
            background: tokens.surface,
            borderRadius: `${tokens.radius}px`,
            boxShadow: tokens.shadowSm,
            overflow: 'hidden',
          }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              borderBottom: `1px solid ${tokens.divider}`,
              pl: { xs: 0, sm: 1.5 },
              minHeight: 'auto',
              '& .MuiTab-root': {
                minHeight: 'auto',
                padding: { xs: '12px 14px', sm: '14px 20px' },
                fontSize: 13,
                fontWeight: 500,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: tokens.textSecondary,
                whiteSpace: 'nowrap',
                gap: 1,
                '&.Mui-selected': { color: tokens.primary },
              },
              '& .MuiTabs-indicator': { background: tokens.primary, height: 2 },
            }}>
            <Tab
              value="access"
              icon={<Folder sx={{ fontSize: 16 }} />}
              iconPosition="start"
              label={
                <TabLabel
                  active={activeTab === 'access'}
                  count={totalAccessCount}>
                  Tilganger
                </TabLabel>
              }
            />
            <Tab
              value="changes"
              icon={<History sx={{ fontSize: 16 }} />}
              iconPosition="start"
              label={<TabLabel active={activeTab === 'changes'}>Endringer</TabLabel>}
            />
            <Tab
              value="mail"
              icon={<Mail sx={{ fontSize: 16 }} />}
              iconPosition="start"
              label={<TabLabel active={activeTab === 'mail'}>E-poster</TabLabel>}
            />
            <Tab
              value="comments"
              icon={<ChatBubbleOutlined sx={{ fontSize: 16 }} />}
              iconPosition="start"
              label={<TabLabel active={activeTab === 'comments'}>Kommentarer</TabLabel>}
            />
          </Tabs>

          {activeTab === 'access' && <AccessesPanel userId={userId} />}
          {activeTab === 'changes' && <ChangesPanel userId={userId} />}
          {activeTab === 'mail' && <NotificationsPanel userId={userId} />}
          {activeTab === 'comments' && <CommentsPanel userId={userId} />}
        </Box>
      </Box>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={emailErrorOpen}
        autoHideDuration={3000}
        onClose={() => setEmailErrorOpen(false)}
        message="E-posten er allerede brukt."
        action={
          <IconButton color="inherit" size="small" onClick={() => setEmailErrorOpen(false)}>
            <Close fontSize="small" />
          </IconButton>
        }
      />
    </Box>
  );
}

function TabLabel({ active, count, children }: { active: boolean; count?: number; children: React.ReactNode }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      {children}
      {count !== undefined && (
        <Chip
          size="small"
          label={count}
          sx={{
            height: 18,
            background: active ? tokens.primaryTint : '#EEF1F3',
            color: active ? tokens.primaryDark : tokens.textSecondary,
            fontSize: 11,
            fontWeight: 600,
            borderRadius: 999,
            '& .MuiChip-label': { px: 0.875 },
          }}
        />
      )}
    </Box>
  );
}
